import React, { Component } from 'react';
import cx from 'classnames';
import { connect } from 'react-redux';
import { saveAs } from 'file-saver';
import { formattedNumberWithZero } from 'helpers/utils';

import LocalizedMessage, { localizeMessage } from 'components/LocalizedMessage';
import classes from '../AnnualPlan.module.scss';
import { OlvCalculated } from 'routes/AnnualPlan/components/AnnualPlan';
import { OlvFrontendRepresentation } from 'types';


interface IProps {
  olvCalculated: OlvCalculated;
  isRenamingUIUser: boolean;
}

class OLVDealCalculation extends Component<IProps> {
  reachIndexesArray = Array.from({ length: 6 });

  exportToExcel = () => {
    const { platforms, total } = this.props.olvCalculated;
    const custom = Object.keys(platforms[0]?.customColumns || {});

    const headers = [
      '',
      localizeMessage({ id: 'budget' }),
      localizeMessage({ id: 'annualPlan.OLVdealCalculation.shows' }),
      localizeMessage({ id: 'trp' }),
      ...this.reachIndexesArray.map((_, index) => `${localizeMessage({ id: 'reach' })} ${index + 1}+`),
      ...custom
    ];

    const rows = platforms.map(row => this.getRowData(row, custom));
    const totalRow = this.getRowData(
      { ...total, name: `${localizeMessage({ id: 'total' })} OLV` },
      custom
    );

    const data = [headers, ...rows, totalRow];

    const ws = XLSX.utils.aoa_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'OLV Deal Calculation');

    const wbout = XLSX.write(wb, { bookType: 'xlsx', type: 'binary' });

    // Convert binary string to ArrayBuffer
    const buf = new ArrayBuffer(wbout.length);
    const view = new Uint8Array(buf);
    for (let i = 0; i < wbout.length; i++) {
      view[i] = wbout.charCodeAt(i) & 0xFF;
    }

    const blob = new Blob([buf], { type: 'application/octet-stream' });
    saveAs(blob, 'olv_deal_calculation.xlsx');
  };

  getRowData = (row: OlvFrontendRepresentation & { name: string }, customColumns: string[]) => [
    row.name,
    formattedNumberWithZero(row.budget),
    formattedNumberWithZero(row.impression),
    formattedNumberWithZero(row.trp),
    ...this.reachIndexesArray.map((_, index) => `${row.reachAll && row.reachAll[index]
      ? formattedNumberWithZero(row.reachAll[index].reachPercent)
      : 0
    }%`
    ),
    ...customColumns.map(key => row.customColumns && row.customColumns[key] || '-')
  ];

  render () {
    const { platforms, total } = this.props.olvCalculated;
    const { isRenamingUIUser } = this.props;
    const totalSuffix = isRenamingUIUser ? '' : ' OLV';
    const updatedTotal = { ...total, name: `${localizeMessage({ id: 'total' })}${totalSuffix}` };


    const custom = Object.keys(platforms[0].customColumns || {});

    return (
      <div className='row'>
        <div className='col-lg-12'>
          <div className='ibox'>
            {/* eslint-disable-next-line max-len */}
            <div className='ibox-title' style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <h5>
                <LocalizedMessage id={isRenamingUIUser ? 'annualPlan.OLVdealCalculation.title.renaming' : 'annualPlan.OLVdealCalculation.title'} />
              </h5>
              <span onClick={this.exportToExcel} style={{ cursor: 'pointer' }}>
                <span className='btn btn-info btn-sm'>
                  <i className='fa fa-file-excel-o fa-lg' />
                  {' '}
                  <span>
                    <LocalizedMessage id='annualPlan.chartsPanel.exportChart' />
                  </span>
                </span>
              </span>
            </div>
            <div className='ibox-content'>
              <table className={cx(
                'table',
                classes.table,
                'table-bordered',
                classes['centered-cells'],
              )}
              >
                <thead>
                  <tr>
                    <th className={cx(classes['month-budget-column'])} />
                    <th className={cx(classes['month-budget-column'])}>
                      <LocalizedMessage id='budget' />
                    </th>
                    <th className={cx(classes['month-budget-column'])}>
                      <LocalizedMessage id='annualPlan.OLVdealCalculation.shows' />
                    </th>
                    <th className={cx(classes['month-budget-column'])}>
                      <LocalizedMessage id='trp' />
                    </th>
                    {this.reachIndexesArray.map((_, index) => (
                      <th key={index} className={cx(classes['month-budget-column'])}>
                        <LocalizedMessage id='reach' />
                        {` ${index + 1}+`}
                      </th>
                    ))}
                    {custom.map(it => <th>{it}</th>)}
                  </tr>
                </thead>
                <tbody>
                  {platforms.map(this.renderRow)}
                  {this.renderRow(updatedTotal, 'total')}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    );
  }

  renderRow = (row: OlvFrontendRepresentation & { name: string}, key) => {
    const customColumns = Object.keys(row.customColumns || {});


    return (
      <tr key={key}>
        <td>
          {row.name}
        </td>
        <td>
          {formattedNumberWithZero(row.budget)}
        </td>
        <td>
          {formattedNumberWithZero(row.impression)}
        </td>
        <td>
          {formattedNumberWithZero(row.trp)}
        </td>
        {this.reachIndexesArray.map((_, index) => (
          <td key={index}>
            {`${row.reachAll && row.reachAll[index]
              ? formattedNumberWithZero(row.reachAll[index].reachPercent)
              : 0
            }%`}
          </td>
        ))}
        {customColumns.map(key => <td>{row.customColumns && row.customColumns[key] || '-'}</td>)}
      </tr>
    );
  };
}

const mapStateToProps = (state) => ({
  isRenamingUIUser: state.auth.profile?.isRenamingUIUser || false,
});

export default connect(mapStateToProps)(OLVDealCalculation);
